<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';

// "O que fazer AGORA": filas que precisam de acao, 1 clique leva a lista.
const props = defineProps({
  live: {
    type: Object,
    default: null,
  },
  kanbanQuick: {
    type: Object,
    default: null,
  },
  activities: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

// Uma linha por funil (ate 3, vindos do servidor), cada uma abrindo o funil ja filtrado; com um funil so, o texto
// de sempre.
const dueRows = (
  total,
  boards,
  { dueStatus, icon, tone, label, boardLabel }
) => {
  if (!total) return [];
  return (boards || []).map(board => ({
    key: `tasks_${dueStatus}_${board.id}`,
    icon,
    tone,
    label:
      boards.length === 1 ? label(total) : boardLabel(board.count, board.name),
    to: {
      name: 'kanban_board_show',
      params: { boardId: board.id },
      query: { due_status: dueStatus },
    },
  }));
};

const rows = computed(() => {
  const items = [];
  const live = props.live || {};
  const quick = props.kanbanQuick;

  if (live.unattended) {
    items.push({
      key: 'unattended',
      icon: 'i-lucide-clock-alert',
      tone: 'text-n-ruby-11',
      label: t('VINCTO_HOME.ACTIONS.UNATTENDED', { count: live.unattended }),
      to: { name: 'conversation_unattended' },
    });
  }
  if (live.unassigned) {
    items.push({
      key: 'unassigned',
      icon: 'i-lucide-user-x',
      tone: 'text-n-amber-11',
      label: t('VINCTO_HOME.ACTIONS.UNASSIGNED', { count: live.unassigned }),
      to: { name: 'home' },
    });
  }
  items.push(
    ...dueRows(quick?.overdue, quick?.overdue_boards, {
      dueStatus: 'overdue',
      icon: 'i-lucide-calendar-x',
      tone: 'text-n-ruby-11',
      label: count => t('VINCTO_HOME.ACTIONS.TASKS_OVERDUE', { count }),
      boardLabel: (count, board) =>
        t('VINCTO_HOME.ACTIONS.TASKS_OVERDUE_IN_BOARD', { count, board }),
    }),
    ...dueRows(quick?.due_today, quick?.due_today_boards, {
      dueStatus: 'due_today',
      icon: 'i-lucide-calendar-clock',
      tone: 'text-n-amber-11',
      label: count => t('VINCTO_HOME.ACTIONS.TASKS_DUE_TODAY', { count }),
      boardLabel: (count, board) =>
        t('VINCTO_HOME.ACTIONS.TASKS_DUE_TODAY_IN_BOARD', { count, board }),
    })
  );
  if (props.activities?.today_count) {
    items.push({
      key: 'activities_today',
      icon: 'i-lucide-calendar-check',
      tone: 'text-n-blue-11',
      label: t('VINCTO_HOME.ACTIONS.ACTIVITIES_TODAY', {
        count: props.activities.today_count,
      }),
      to: { name: 'crm_calendar' },
    });
  }
  return items;
});
</script>

<template>
  <KanbanReportWidget
    class="!rounded-2xl !border-n-weak !bg-white dark:!bg-n-solid-2"
    :title="t('VINCTO_HOME.ACTIONS.TITLE')"
    :info-text="t('VINCTO_HOME.ACTIONS.INFO')"
    :is-loading="isLoading"
    :is-empty="!rows.length"
    :empty-text="t('VINCTO_HOME.ACTIONS.EMPTY')"
  >
    <ul class="flex flex-col divide-y divide-n-weak -my-2">
      <li v-for="row in rows" :key="row.key">
        <router-link :to="row.to" class="flex items-center gap-3 py-2.5 group">
          <i :class="[row.icon, row.tone]" class="w-4 h-4 flex-shrink-0" />
          <span
            class="flex-1 text-sm text-n-slate-12 group-hover:text-n-blue-11 truncate"
          >
            {{ row.label }}
          </span>
          <i
            class="i-lucide-chevron-right w-4 h-4 text-n-slate-8 group-hover:text-n-blue-11"
          />
        </router-link>
      </li>
    </ul>
  </KanbanReportWidget>
</template>
